import { css } from "@linaria/core"
import { Stack, Typography } from "@mui/material"

import { DcaOrderFormValues } from "./types/DcaOrderForm"
import { LimitOrderFormValues } from "./types/LimitOrderForm"
import { StopLosOrderFormValues } from "./types/StopLossOrderForm"
import { CONFIRM_DIALOG_TITLES, OrderType } from "./types/swap-types"
import { TokenInfo } from "../../ao/token"
import { NumberFont, TitleFont } from "../../theme"
import { formatDuration, formatNumberAuto, formatTicker } from "../../utils/format"
import TokenLogo from "../TokenLogo"
import { TokenPill } from "../TokenPill"

type SwapOrderPreviewFormProps = {
  orderType: OrderType
  orderExtra: LimitOrderFormValues | DcaOrderFormValues | StopLosOrderFormValues | null
  fromToken: TokenInfo
  toToken: TokenInfo
  fromValue: string
  toValue: string
}

export function SwapOrderPreviewForm(props: SwapOrderPreviewFormProps) {
  const { orderType, orderExtra, fromValue, toValue, fromToken, toToken } = props

  return (
    <>
      <Typography variant="h5" fontFamily={TitleFont}>
        {CONFIRM_DIALOG_TITLES[orderType]}
      </Typography>
      <div
        className={css`
          display: grid;
          grid-template-columns: 1fr 48px 1fr;
          align-items: flex-start;
          padding-bottom: calc(var(--spacing) * 2);
          border-bottom: 1px solid var(--mui-palette-borderSecondary);
          & > div {
            overflow: hidden;
          }
        `}
      >
        {fromToken && (
          <Stack gap={1}>
            <Typography color="text.secondary">
              {orderType === "dca" ? "I want to allocate" : "You Sell"}
            </Typography>
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography
                fontFamily={NumberFont}
                variant="h3"
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
                data-testid="fromTokenAmount"
              >
                {formatNumberAuto(fromValue)}
              </Typography>
              <TokenPill
                variant="outlined"
                className={css`
                  min-width: fit-content;
                  padding: calc(var(--spacing)) calc(var(--spacing) * 1.25) !important;
                `}
              >
                <TokenLogo tx={fromToken.logo} alt={`${fromToken.name} logo`} size="medium" />
                <span data-testid="fromTokenTicker">{formatTicker(fromToken.ticker)}</span>
              </TokenPill>
            </Stack>
          </Stack>
        )}
        <div />
        {toToken && (
          <Stack gap={1}>
            <Typography color="text.secondary">
              {orderType === "dca" ? "To Buy" : "You Buy"}
            </Typography>
            <Stack direction="row" alignItems="center" gap={2}>
              {orderType === "dca" ? (
                <Typography
                  fontFamily={NumberFont}
                  variant="h3"
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  for {formatDuration((orderExtra as DcaOrderFormValues)?.runningTime)}
                </Typography>
              ) : orderType === "stop-loss" &&
                (orderExtra as StopLosOrderFormValues)?.variant !== "limit" ? (
                <Typography
                  fontFamily={NumberFont}
                  variant="h3"
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  at market price
                </Typography>
              ) : (
                <Typography
                  fontFamily={NumberFont}
                  variant="h3"
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                  data-testid="toTokenAmount"
                >
                  {formatNumberAuto(toValue)}
                </Typography>
              )}
              <TokenPill
                variant="outlined"
                className={css`
                  min-width: fit-content;
                  padding: calc(var(--spacing)) calc(var(--spacing) * 1.25) !important;
                `}
              >
                <TokenLogo tx={toToken.logo} alt={`${toToken.name} logo`} size="medium" />
                <span data-testid="toTokenTicker">{formatTicker(toToken.ticker)}</span>
              </TokenPill>
            </Stack>
          </Stack>
        )}
      </div>
    </>
  )
}
